<script setup lang="ts">
import type { CookieApp } from '../types/cookies';
import { Dropdown } from 'floating-vue';
import { useHelpPortalURL, useLegalPortalURL } from '../composables/portalUrls';
import { useBottomDialog } from '../composables/useBottomDialog';
import DropdownDivider from './Dropdown/Divider.vue';
import DropdownItem from './Dropdown/Item.vue';
import Icon from './Icon.vue';

type HelpPortalURL = Parameters<typeof useHelpPortalURL>[0];
type LegalPortalURL = Parameters<typeof useLegalPortalURL>[0];

const props = withDefaults(defineProps<{
	app?: CookieApp;
	isLight?: boolean;
	isAbsolute?: boolean;
	hasHelp?: boolean;
}>(), {
	hasHelp: true,
});

const imprintDialog = useBottomDialog({
	key: 'imprint',
	listen: false,
});
const cookieSettingsDialog = useBottomDialog<CookieApp>({
	key: 'cookie-settings',
	listen: false,
});
</script>

<template>
	<div
		class="copyright"
		:class="[{
			'copyright--is-light': props.isLight,
			'copyright--is-absolute': props.isAbsolute,
		}]"
	>
		<span class="copyright__text">&copy; Hahnemühle {{ new Date().getFullYear() }}</span>
		<ClientOnly v-if="props.hasHelp">
			<span class="copyright__dot"></span>
			<Dropdown position="top">
				<button type="button" class="copyright__button">
					<Icon name="help" />
					<span>Help</span>
				</button>

				<template #popper>
					<DropdownItem
						label="Help center"
						icon="help"
						size="sm"
						:to="useHelpPortalURL() as HelpPortalURL"
						target="_blank"
					/>
					<DropdownDivider has-padding />
					<DropdownItem
						label="Privacy policy"
						icon="file-shield"
						size="sm"
						:to="useLegalPortalURL('privacy-policy') as LegalPortalURL"
						target="_blank"
					/>
					<DropdownItem
						label="Terms of use"
						icon="files"
						size="sm"
						:to="useLegalPortalURL('general-terms-and-conditions') as LegalPortalURL"
						target="_blank"
					/>
					<DropdownDivider has-padding />
					<DropdownItem
						label="Imprint"
						icon="brand-hahnemuehle-logo"
						size="sm"
						v-close-popper
						@click="imprintDialog.setDialogVisibility(true)"
					/>
					<DropdownItem
						label="Cookie settings"
						icon="cookie"
						size="sm"
						v-if="props.app"
						v-close-popper
						@click="cookieSettingsDialog.setDialogVisibility(true, props.app)"
					/>
				</template>
			</Dropdown>
		</ClientOnly>
	</div>
</template>

<style scoped lang="scss">
@layer components
{
	.copyright
	{
		@include flex(null, center, var(--spacing-2xs));

		&__text
		{
			@include flex(null, center, var(--spacing-2xs));
			color:var(--c-caption);
			@include text-caption;
		}
		&__dot
		{
			display:block;
			@include size(2);
			border-radius:var(--radius-sm);
			background-color:var(--c-caption);
		}
		&__button
		{
			@include flex(center, center, var(--spacing-4xs));
			color:var(--c-caption);
			@include text-caption;
			border-radius:var(--radius-full);
			background-color:color-opacity(var(--c-caption), 4);
			padding:var(--spacing-4xs) var(--spacing-2xs);
			transition:var(--transition-default);

			@include button-interaction(var(--fill-c-light), 4);

			&:deep(.icon)
			{
				@include svg(16);
			}
		}

		&--is-light
		{
			.copyright
			{
				&__text
				{
					color:var(--c-caption-light);

					&::after
					{
						background-color:var(--c-caption-light);
					}
				}
				&__button
				{
					color:var(--c-caption-light);
					background-color:color-opacity(var(--border-secondary), 4);
				}
			}
		}
		&--is-absolute
		{
			position:absolute;
			inset:auto auto var(--spacing-xl) var(--spacing-md);
			z-index:1;

			@include responsive(md)
			{
				inset-inline-start:var(--spacing-2xl);
				inset-block-end:var(--spacing-2xl);
			}
		}
	}
}
</style>
